/**
 * Rollen im Workspace (Konzept: „Rollen, Rechte und Teilen mit Gästen“). Gäste (P15) sehen nur die Projekte bzw.
 * Bereiche, in die sie eingeladen sind – keine Mitgliederliste, keine Projekte anlegen, nie einladen.
 * Instanz-Admin ist keine Workspace-Rolle, sondern ein Merkmal des Kontos.
 */
export const WORKSPACE_ROLES = ['guest', 'member', 'admin'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];
/** Rollen, die über die Workspace-Verwaltung vergeben werden (Gäste kommen über Projekte) */
export const MEMBER_ROLES = ['member', 'admin'] as const;

const rank: Record<WorkspaceRole, number> = { guest: 0, member: 1, admin: 2 };

/**
 * Stufen für Gäste (P15) und – seit 0.66 (ADR 0063) – eingeschränkte Mitglieder: Ansehen, Kommentieren (Kommentare,
 * Antworten, Chat, Reaktionen), Ausführen (sichtbare Aufgaben erledigen/wieder öffnen, Status, Checklisten abhaken),
 * Bearbeiten (Aufgaben, Dateien, Seiten). Löschen, Einstellungen und Einladen nie.
 */
export const GUEST_ACCESS = ['view', 'comment', 'execute', 'edit'] as const;
export type GuestAccess = (typeof GUEST_ACCESS)[number];
const guestRank: Record<GuestAccess, number> = { view: 0, comment: 1, execute: 2, edit: 3 };
export function guestAccessAtLeast(level: GuestAccess, needed: GuestAccess): boolean {
  return guestRank[level] >= guestRank[needed];
}

export function workspaceRoleAtLeast(role: WorkspaceRole | null | undefined, needed: WorkspaceRole): boolean {
  return !!role && rank[role] >= rank[needed];
}

/**
 * Ein Workspace braucht immer mindestens eine aktive Administration. Prüft, ob eine Änderung die letzte
 * aktive Admin-Rolle entfernen würde.
 */
export function wouldRemoveLastAdmin(
  members: ReadonlyArray<{ userId: string; role: WorkspaceRole; suspended: boolean }>,
  change: { userId: string; role?: WorkspaceRole; suspended?: boolean; removed?: boolean },
): boolean {
  const after = members
    .map((m) => (m.userId !== change.userId ? m : change.removed ? null : { ...m, ...change }))
    .filter((m): m is { userId: string; role: WorkspaceRole; suspended: boolean } => !!m);
  return !after.some((m) => m.role === 'admin' && !m.suspended);
}

/**
 * Abnahme 0.58 N01: Linkgäste – leichte Konten ohne E-Mail und Passwort, entstanden beim ersten Öffnen eines
 * Projektlinks. Erkennbar an dieser nicht zustellbaren Adresse (RFC 2606 `.invalid`); nie Mail an sie senden.
 */
export const LINK_GUEST_DOMAIN = 'link.werft.invalid';
export const isLinkGuestEmail = (email: string | null | undefined): boolean => !!email && email.toLowerCase().endsWith(`@${LINK_GUEST_DOMAIN}`);
